import { useEffect, useRef, useState, type RefObject } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';

/**
 * Testing-only counterpart to apps/mobile's `/impersonateUser` route,
 * reached via `ImpersonateUserPanel`'s destination picker. Redeems
 * `?ticket=` and lands the impersonated session in this app.
 */
export default function ImpersonateUser(): React.JSX.Element {
  const signInState: ReturnType<typeof useSignIn> = useSignIn();
  const hasRun: RefObject<boolean> = useRef<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!signInState.isLoaded || hasRun.current) return;

    const ticket: string | null = new URLSearchParams(window.location.search).get('ticket');
    if (!ticket) return;

    hasRun.current = true;
    const { signIn, setActive } = signInState;

    const redeemTicket = async (): Promise<void> => {
      const attempt: Awaited<ReturnType<typeof signIn.create>> = await signIn.create({
        strategy: 'ticket',
        ticket,
      });
      if (attempt.status !== 'complete' || !attempt.createdSessionId) {
        console.error(
          '[ImpersonateUser] sign-in not complete after ticket redemption',
          attempt.status
        );
        setError('Sign-in did not complete.');
        return;
      }
      await setActive({ session: attempt.createdSessionId });
      window.location.href = '/';
    };

    void redeemTicket();
  }, [signInState]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background">
      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : (
        <Loader2 className="animate-spin" size={32} />
      )}
    </div>
  );
}
